import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { capitalize } from 'lodash';

import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';

import { IconTrash, IconArrowUp, IconArrowDown, IconX } from '@tabler/icons-react';

import {
  CampaignDto,
  CampaignListItemDto,
  CampaignStatus as CampaignStatusEnum,
  useCampaignDeleteControllerDeleteCampaignMutation,
  useCampaignStopControllerCampaignStopMutation,
  useCampaignRunControllerCampaignRunMutation,
  apiService,
} from '@shared/services/apiService/apiService';
import BlankCard from '@shared/components/shared/BlankCard';
import { CampaignListProps, SortingCampaignCoulmns } from '@shared/types/admin/campaign';

import { useStyles } from './styles';
import CampaignStatusButton, { CampaignStatus } from './CampaignStatusButton/CampaignStatusButton';
import { getCampaignStatus } from './CampaignStatusButton/utils';
import Spinner from '@shared/components/spinner/Spinner';
import { ConfirmDeleteDialog } from '@shared/components/confirmDeleteDialog/ConfirmDeleteDialog';
import { ConfirmDialog } from '@shared/components/confirmDialog/ConfirmDialog';
import { formatTimeWithUnits } from '@shared/utils/FormatTime';
import { getNextAvailableCallTimestamp, isOutOfSchedule } from '@shared/utils/DateUtil';
import { actions } from '@shared/store/ducks';
import { RootState, useAppDispatch, useAppSelector } from '@shared/store';
import NewCampaignDialog from './NewCampaignDialog';
import { CampaignTypeEnum } from '@shared/constants/campaign.constants';
import { USAGE_LIMIT_WARNING_THRESHOLD_SECONDS } from '@pages/Admin/Campaign/ui/constants';
import { getRemainingCallSeconds } from '@pages/Admin/Campaign/ui/utils';
import {
  IconCircleArrowLeft,
  IconCircleArrowRight,
  IconExternalLinkOutlined,
} from '@shared/assets/images/svgs';
import { firstSymbolToUpperCase } from '@shared/layouts/FullLayout/shared/breadcrumb/utils';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { useWebSocketGetCampaigns } from '@features/Admin/Campaign/hooks/useWebSocketGetCampaigns';

type CampaignListRow = CampaignListItemDto & Partial<CampaignDto>;

export const CampaignList: React.FC<CampaignListProps> = ({
  setLoading,
  onCampaignDeleteError,
}: CampaignListProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigator = useNavigate();
  const [useDeleteCampaign] = useCampaignDeleteControllerDeleteCampaignMutation();

  const [useRunCampaign, { isLoading: isRunningLoading }] =
    useCampaignRunControllerCampaignRunMutation();
  const [useStopCampaign, { isLoading: isStoppingLoading }] =
    useCampaignStopControllerCampaignStopMutation();
  const [fetchUsageLimits] = apiService.endpoints.usageLimitControllerUsageLimits.useLazyQuery();

  const [sortBy, setSortBy] = useState<SortingCampaignCoulmns>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewCampaignDialog, setShowNewCampaignDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showScheduleWarningDilaogFlag, setShowScheduleWarningDialogFlag] = useState(false);
  const [timeToStart, setTimeToStart] = useState<number>(new Date().getTime());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteId, setDeleteId] = useState<string | null>();
  const [campaignToRun, setCampaignToRun] = useState<CampaignListRow>();
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [showUsageLimitWarningDialog, setShowUsageLimitWarningDialog] = useState(false);
  const [showUsageLimitBlockedDialog, setShowUsageLimitBlockedDialog] = useState(false);
  const pendingCampaignToRunRef = useRef<CampaignListRow | undefined>(undefined);
  const isClosingActionsRef = useRef(false);
  const loadMoreAnchorRef = useRef<HTMLDivElement | null>(null);

  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);

  const {
    data: campaigns,
    isLoading: isCampaignListLoading,
    isFetching: isCampaignListFetching,
    hasMore: hasMoreCampaigns,
    loadMore,
    refetch: refetchCampaignsList,
  } = useWebSocketGetCampaigns(undefined, {
    limit: 20,
    sortBy,
    sortOrder: sortOrder.toUpperCase() as 'ASC' | 'DESC',
    withTotal: true,
    companyId: currentUser?.companyId,
    search: searchQuery || undefined,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    dispatch(actions.breadcrumbs.setIsShownBreadcrumbs(true));
    dispatch(actions.customizer.toggleEditCampaignView(false));
    dispatch(
      actions.breadcrumbs.setBreadcrumb({
        pageTitle: 'campaignPage.header.campaign',
        campaignType: null,
        icon: 'IconCampaignList',
      }),
    );
    // TODO: hide page title until other designs are not done
    return () => {
      dispatch(actions.breadcrumbs.setIsShownBreadcrumbs(false));
    };
  }, [dispatch]);

  const tryLoadMoreCampaigns = useCallback(() => {
    if (!hasMoreCampaigns || isCampaignListFetching) return;
    void loadMore();
  }, [hasMoreCampaigns, isCampaignListFetching, loadMore]);

  const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
    let current = node?.parentElement || null;
    while (current) {
      const styles = window.getComputedStyle(current);
      const overflowY = styles.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  };

  useEffect(() => {
    const anchor = loadMoreAnchorRef.current;
    if (!anchor) return;
    const scrollRoot = getScrollParent(anchor);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        tryLoadMoreCampaigns();
      },
      { root: scrollRoot, rootMargin: '240px' },
    );
    observer.observe(anchor);
    return () => observer.disconnect();
  }, [tryLoadMoreCampaigns]);

  useEffect(() => {
    const anchor = loadMoreAnchorRef.current;
    if (!anchor) return;
    const scrollRoot = getScrollParent(anchor);

    const onScroll = () => {
      const anchorRect = anchor.getBoundingClientRect();
      if (scrollRoot) {
        const rootRect = scrollRoot.getBoundingClientRect();
        if (anchorRect.top - rootRect.bottom < 240) {
          tryLoadMoreCampaigns();
        }
        return;
      }
      if (anchorRect.top - window.innerHeight < 240) {
        tryLoadMoreCampaigns();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    scrollRoot?.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      scrollRoot?.removeEventListener('scroll', onScroll);
    };
  }, [tryLoadMoreCampaigns]);

  const handleSort = (field: SortingCampaignCoulmns) => {
    if (sortBy !== field) {
      setSortBy(field);
      setSortOrder('desc');
      return;
    }
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const statusBadge = (status?: CampaignStatusEnum) => {
    const colorMap = {
      active: theme.palette.success.light,
      inactive: theme.palette.extendedGrey?.lightest,
      finishing: theme.palette.extendedGrey?.lightest,
    };
    return colorMap?.[status!] || theme.palette.extendedGrey?.lightest;
  };

  const statusBadgeText = (status?: CampaignStatusEnum) => {
    const colorMap = {
      active: theme.palette.success.dark,
      inactive: theme.palette.extendedGrey?.dark,
      finishing: theme.palette.extendedGrey?.dark,
    };

    return colorMap?.[status!] || theme.palette.text.secondary;
  };

  const isOutboundWithoutCustomers = (campaign: CampaignListRow) =>
    campaign.type === CampaignTypeEnum.OUTBOUND && !campaign.customersTotal;

  const getDisabledReason = (campaign: CampaignListRow) => {
    if (currentUser?.role === UserRolesEnum.VIEWER) {
      return t('adminMainPage.campaigns.validation.noPermissionToStart');
    }
    if (isRunningLoading || isStoppingLoading) {
      return t('adminMainPage.campaigns.validation.campaignStarting');
    }
    if (getCampaignStatus(campaign) === CampaignStatus.NotStarted) {
      if (!campaign.agentsCount) {
        return t('adminMainPage.campaigns.validation.noAgents');
      }
      if (isOutboundWithoutCustomers(campaign)) {
        return t('adminMainPage.campaigns.validation.noPhoneNumbers');
      }
    }
    return '';
  };

  const getSortHeaderColor = (field: SortingCampaignCoulmns) =>
    sortBy === field ? '#2A3547' : theme.palette.text.secondary;

  const renderSortIcon = (isDescending: boolean) => (
    <Box className={classes.campagin_table_sort_icon}>
      {isDescending ? <IconArrowDown size={12} /> : <IconArrowUp size={12} />}
    </Box>
  );

  const formatCreatedDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
      .format(date)
      .replace(/\//g, '.');
  };

  const proceedWithScheduleCheck = async (campaign: CampaignListRow) => {
    const schedule = campaign.callSchedule;
    const nextAvailableCallTimestamp = getNextAvailableCallTimestamp(schedule);

    const canRun = !schedule || !isOutOfSchedule(schedule);
    if (canRun) {
      await useRunCampaign({ id: campaign.id });
    } else {
      setCampaignToRun(campaign);
      if (nextAvailableCallTimestamp) {
        setTimeToStart(nextAvailableCallTimestamp);
        setShowScheduleWarningDialogFlag(true);
      }
    }
  };

  const handleManagedStartStop = async (id: string, campaignStatus: CampaignStatus) => {
    if (CampaignStatus.NotStarted === campaignStatus) {
      const campaign = campaigns.find((c) => c.id === id);
      if (!campaign) return;

      try {
        const result = await fetchUsageLimits(undefined, false).unwrap();

        const remaining = getRemainingCallSeconds(result);

        if (remaining <= 0) {
          setShowUsageLimitBlockedDialog(true);
          return;
        }

        if (remaining <= USAGE_LIMIT_WARNING_THRESHOLD_SECONDS) {
          pendingCampaignToRunRef.current = campaign;
          setShowUsageLimitWarningDialog(true);
          return;
        }
      } catch {
        setShowUsageLimitBlockedDialog(true);
        return;
      }

      await proceedWithScheduleCheck(campaign);
    } else if (CampaignStatus.Dialing === campaignStatus) {
      await useStopCampaign({ id });
    }
  };

  const handleUsageLimitWarningConfirm = async () => {
    setShowUsageLimitWarningDialog(false);
    const campaign = pendingCampaignToRunRef.current;
    if (campaign) {
      pendingCampaignToRunRef.current = undefined;
      await proceedWithScheduleCheck(campaign);
    }
  };

  const handleDeleteBtn = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setShowDialog(true);

    if (selectedRowId) {
      setDeleteId(selectedRowId);
    }

    setAnchorEl(null);
  };

  const deleteCampaign = async () => {
    if (deleteId) {
      setShowDialog(false);
      setLoading(true);
      const response = await useDeleteCampaign({
        id: deleteId,
      });

      if ('error' in response) {
        const errorText = (response.error as { data: { message: string } }).data.message;

        onCampaignDeleteError({
          message: errorText,
          type: 'error',
        });
      } else {
        onCampaignDeleteError({
          message: t('adminMainPage.campaigns.successfullyDeleted'),
          type: 'success',
        });
      }
    }
    await refetchCampaignsList();
    setDeleteId('');
    setLoading(false);
  };

  const handleMoreBtn = (event: any, rowId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handlePopoverAction = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const rowId = selectedRowId;
    handleClosePopover();
    if (rowId) {
      navigator(`/admin/campaign/${rowId}/edit`);
    }
  };

  const handleClosePopover = () => {
    isClosingActionsRef.current = true;
    setTimeout(() => {
      isClosingActionsRef.current = false;
    }, 0);
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleRowClick = (rowId: string) => {
    if (isClosingActionsRef.current) return;
    if (anchorEl) {
      handleClosePopover();
      return;
    }
    navigator(`/admin/campaign/${rowId}/edit`);
  };

  const runCampaign = async () => {
    setShowScheduleWarningDialogFlag(false);

    if (campaignToRun?.id) {
      await useRunCampaign({ id: campaignToRun?.id });
    }
  };

  if (isCampaignListLoading && campaigns.length === 0) {
    return <Spinner />;
  }

  return (
    <BlankCard className={classes.campaign}>
      <Box className={classes.campaign_header}>
        <TextField
          className={classes.campaign_search_field}
          size="small"
          placeholder={t('adminMainPage.campaigns.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t('adminMainPage.campaigns.searchPlaceholder')}
                  onClick={() => setSearchInput('')}>
                  <IconX size={16} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Tooltip title={t('adminMainPage.operation.addCampaign')} arrow>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ backgroundColor: theme.palette.primary.main }}
            disabled={currentUser?.role === UserRolesEnum.VIEWER}
            onClick={() => {
              setShowNewCampaignDialog(true);
            }}>
            {t('adminMainPage.operation.addCampaign')}
          </Button>
        </Tooltip>
      </Box>
      <TableContainer>
        <Table className={classes.campaign_table}>
          <TableHead
            sx={{
              backgroundColor: theme.palette.extendedGrey?.lighter,
              height: '48px',
              borderTop: `1px solid ${theme.palette.divider}`,
            }}>
            <TableRow>
              <TableCell>
                <Box className={classes.campagin_table_sort_cell}>
                  <Typography
                    variant="h6"
                    color={theme.palette.text.secondary}
                    className={classes.campagin_table_th}>
                    {t('adminMainPage.campaigns.type')}
                  </Typography>
                </Box>
              </TableCell>

              <TableCell
                onClick={() => handleSort('title')}
                className={classes.campaign_table_first_cell}>
                <Box className={classes.campagin_table_sort_cell}>
                  <Typography
                    variant="h6"
                    className={classes.campagin_table_th}
                    color={getSortHeaderColor('title')}>
                    {t('adminMainPage.campaigns.campaignName')}
                  </Typography>
                  {sortBy === 'title' ? renderSortIcon(sortOrder === 'desc') : null}
                </Box>
              </TableCell>

              <TableCell className={classes.campaign_table_status_cell}>
                <Box>
                  <Typography
                    color={theme.palette.text.secondary}
                    variant="h6"
                    className={classes.campagin_table_th}>
                    {t('adminMainPage.campaigns.status')}
                  </Typography>
                </Box>
              </TableCell>
              {currentUser?.role !== UserRolesEnum.VIEWER && (
                <TableCell className={classes.campaign_table_startstop_cell}>
                  <Typography
                    color={theme.palette.text.secondary}
                    variant="h6"
                    className={classes.campagin_table_th}
                    align="center">
                    {t('adminMainPage.operation.startStop')}
                  </Typography>
                </TableCell>
              )}
              <TableCell
                onClick={() => handleSort('createdAt')}
                className={classes.campaign_table_created_at_cell}>
                <Box className={classes.campagin_table_sort_cell}>
                  <Typography
                    color={getSortHeaderColor('createdAt')}
                    variant="h6"
                    className={classes.campagin_table_th}>
                    {t('adminMainPage.campaigns.createdAt')}
                  </Typography>
                  {sortBy === 'createdAt' ? renderSortIcon(sortOrder === 'desc') : null}
                </Box>
              </TableCell>
              <TableCell>
                <Box className={classes.campagin_table_sort_cell}>
                  <Typography
                    color={theme.palette.text.secondary}
                    variant="h6"
                    className={classes.campagin_table_th}>
                    {t('adminMainPage.campaigns.customersLeft')}
                  </Typography>
                </Box>
              </TableCell>

              <TableCell>
                <Typography className={classes.campagin_table_th} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((row) => (
              <TableRow
                className={classes.campaign_table_row}
                key={row.id}
                hover
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowClick(row.id);
                }}>
                <TableCell>
                  {row.type === CampaignTypeEnum.OUTBOUND ? (
                    <Box
                      className={`${classes.campaign_type_badge} ${classes.campaign_type_badge_outbound}`}>
                      <Typography
                        component="span"
                        className={`${classes.campaign_type_badge_label} ${classes.campaign_type_badge_label_outbound}`}>
                        {firstSymbolToUpperCase(CampaignTypeEnum.OUTBOUND)}
                      </Typography>
                      <Box
                        className={`${classes.campaign_type_badge_icon} ${classes.campaign_type_badge_icon_outbound}`}>
                        <IconCircleArrowRight />
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      className={`${classes.campaign_type_badge} ${classes.campaign_type_badge_inbound}`}>
                      <Box
                        className={`${classes.campaign_type_badge_icon} ${classes.campaign_type_badge_icon_inbound}`}>
                        <IconCircleArrowLeft />
                      </Box>
                      <Typography
                        component="span"
                        className={`${classes.campaign_type_badge_label} ${classes.campaign_type_badge_label_inbound}`}>
                        {firstSymbolToUpperCase(CampaignTypeEnum.INBOUND)}
                      </Typography>
                    </Box>
                  )}
                </TableCell>

                <TableCell className={classes.campaign_table_first_cell}>
                  <Typography variant="subtitle1">{row.title}</Typography>
                </TableCell>
                <TableCell className={classes.campaign_table_status_cell}>
                  <Chip
                    sx={{
                      backgroundColor: statusBadge(row?.status),
                      color: statusBadgeText(row.status),
                    }}
                    size="small"
                    label={capitalize(row?.status || 'inactive')}
                  />
                </TableCell>
                {currentUser?.role !== UserRolesEnum.VIEWER && (
                  <TableCell align="center" className={classes.campaign_table_startstop_cell}>
                    <Tooltip title={getDisabledReason(row)} arrow>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'center' }}
                        onClick={(e) => e.stopPropagation()}>
                        <CampaignStatusButton
                          onClick={handleManagedStartStop}
                          disabled={
                            isRunningLoading ||
                            isStoppingLoading ||
                            currentUser?.role === UserRolesEnum.VIEWER ||
                            (getCampaignStatus(row) === CampaignStatus.NotStarted &&
                              (isOutboundWithoutCustomers(row) || !row.agentsCount))
                          }
                          campaign={row}
                        />
                      </Box>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell className={classes.campaign_table_created_at_cell}>
                  <Typography variant="subtitle1">{formatCreatedDate(row.createdAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle1">
                    {row.customersTotal}/{row.customersLeft}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Tooltip title="More actions">
                    <IconButton
                      aria-describedby={row.id.toString()}
                      disabled={currentUser?.role === UserRolesEnum.VIEWER}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMoreBtn(event, row.id);
                      }}
                      sx={{
                        borderRadius: '8px',
                        '&:hover': {
                          bgcolor: '#1875FF0D',
                        },
                      }}>
                      <MoreVertIcon
                        sx={{
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            color: '#1875FF',
                          },
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box ref={loadMoreAnchorRef} sx={{ height: 1 }} />
      <Box display="flex" justifyContent="center" mt={2} minHeight={24}>
        <Typography className={classes.pagination_text}>
          {isCampaignListFetching
            ? t(
                campaigns.length > 0
                  ? 'adminMainPage.campaigns.loadingMore'
                  : 'adminMainPage.campaigns.searching',
              )
            : ''}
        </Typography>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={(event) => {
          event.stopPropagation();
        }}>
        <Box className={classes.agent_table_popover}>
          <IconButton
            className={`${classes.agent_table_popover_button} ${classes.agent_table_popover_button_open}`}
            onClick={handlePopoverAction}>
            <IconExternalLinkOutlined />
            <Typography>{t('adminMainPage.operation.open')}</Typography>
          </IconButton>
          <IconButton
            className={classes.agent_table_popover_button}
            disabled={currentUser?.role === UserRolesEnum.VIEWER}
            onClick={handleDeleteBtn}>
            <IconTrash size="24px" color="#FA896B" />
            <Typography color="#FA896B">{t('adminMainPage.operation.delete')}</Typography>
          </IconButton>
        </Box>
      </Popover>
      <NewCampaignDialog
        showDialogFlag={showNewCampaignDialog}
        handleClose={setShowNewCampaignDialog}
        showDialog={setShowNewCampaignDialog}
      />
      <ConfirmDeleteDialog
        open={showDialog}
        onClose={setShowDialog}
        dialogTitle={'dialog.title3'}
        dialogContentText={'dialog.content3'}
        showDialog={setShowDialog}
        confirmDelete={deleteCampaign}
      />
      <ConfirmDialog
        showDialogFlag={showScheduleWarningDilaogFlag}
        handleClose={setShowScheduleWarningDialogFlag}
        dialogTitle={'dialog.title4'}
        dialogContentText={t('dialog.content4', {
          timeToStart: formatTimeWithUnits((timeToStart - new Date().getTime()) / 1000),
        })}
        variant="warning"
        showDialog={setShowScheduleWarningDialogFlag}
        confirmDialog={runCampaign}
      />
      <ConfirmDialog
        showDialogFlag={showUsageLimitWarningDialog}
        handleClose={() => setShowUsageLimitWarningDialog(false)}
        dialogTitle={'dialog.usageLimitWarningTitle'}
        dialogContentText={t('dialog.usageLimitWarningContent')}
        showDialog={setShowUsageLimitWarningDialog}
        confirmDialog={handleUsageLimitWarningConfirm}
      />
      <Dialog
        open={showUsageLimitBlockedDialog}
        onClose={() => setShowUsageLimitBlockedDialog(false)}
        aria-labelledby="usage-limit-blocked-title"
        aria-describedby="usage-limit-blocked-description">
        <DialogTitle id="usage-limit-blocked-title">
          {t('dialog.usageLimitBlockedTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="usage-limit-blocked-description">
            {t('dialog.usageLimitBlockedContent')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUsageLimitBlockedDialog(false)}>
            {t('adminMainPage.operation.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </BlankCard>
  );
};
